import {
  ApiClient,
  type ApiClientOptions,
  type CursorPageQueryParams,
  type CursorPagedResponse,
} from '@/infra/api-client'
import { toURLSearchParams } from '@/utils/url-util'
import type {
  ComputeLegendVersionRequest,
  ComputeLegendVersionResultResource,
  EngineSymbolsResource,
  OrganizerBindingCatalogsResource,
} from './resources/engine'
import type {
  CreateLegendRequest,
  LegendActivationResource,
  LegendResource,
  ListLegendsRequest,
  PublishLegendVersionRequest,
  SaveLegendVersionRequest,
  SaveLegendVersionResultResource,
} from './resources/legend'

/**
 * The engine version the builder speaks. The Legend API only returns the
 * versions the builder can edit, and analyzes saves with this version's rules.
 */
export const SUPPORTED_ENGINE_VERSION = '1.39.0'

const supportedEngineVersionParams = {
  supported_engine_version: SUPPORTED_ENGINE_VERSION,
} as const

/**
 * The Legend API, as the legend builder uses it.
 */
export class LegendBuilderApi {
  readonly legends
  readonly versions
  readonly engineSymbols
  readonly organizerBindings

  constructor(options: ApiClientOptions) {
    this.legends = new LegendApi(options)
    this.versions = new LegendVersionApi(options)
    this.engineSymbols = new EngineSymbolsApi(options)
    this.organizerBindings = new OrganizerBindingsApi(options)
  }
}

/**
 * Legends, with their latest versions.
 */
export class LegendApi extends ApiClient {
  /**
   * Lists the latest version of each of the workspace's legends.
   *
   * @param workspaceId
   * @param request
   * @param signal
   */
  list(
    workspaceId: string,
    request: ListLegendsRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<LegendResource>> {
    return this._get(
      `v3/workspaces/${encodeURIComponent(workspaceId)}/legends`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  /**
   * Creates a legend and its first draft.
   *
   * @param request
   */
  create(request: CreateLegendRequest): Promise<LegendResource> {
    return this._post(
      'v3/legends',
      request,
      toURLSearchParams(supportedEngineVersionParams),
    )
  }

  /**
   * Makes the legend available for new jobs.
   *
   * @param id
   */
  activate(id: string): Promise<LegendActivationResource> {
    return this._post(
      `v3/legends/${encodeURIComponent(id)}/activate`,
      undefined,
    )
  }

  /**
   * Stops the legend from being used for new jobs.
   *
   * @param id
   */
  deactivate(id: string): Promise<LegendActivationResource> {
    return this._post(
      `v3/legends/${encodeURIComponent(id)}/deactivate`,
      undefined,
    )
  }
}

/**
 * The versions of a legend.
 */
export class LegendVersionApi extends ApiClient {
  /**
   * Lists the legend's versions, latest first.
   *
   * @param legendId
   * @param request
   * @param signal
   */
  list(
    legendId: string,
    request: CursorPageQueryParams,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<LegendResource>> {
    return this._get(
      `v3/legends/${encodeURIComponent(legendId)}/versions`,
      toURLSearchParams({ ...supportedEngineVersionParams, ...request }),
      { signal },
    )
  }

  /**
   * Gets a version of the legend, with its content.
   *
   * @param legendId
   * @param version
   * @param signal
   */
  get(
    legendId: string,
    version: number | 'latest',
    signal?: AbortSignal,
  ): Promise<LegendResource> {
    return this._get(
      `v3/legends/${encodeURIComponent(legendId)}/versions/${version}`,
      toURLSearchParams(supportedEngineVersionParams),
      { signal },
    )
  }

  /**
   * Saves the version, creating it when it doesn't exist yet.
   *
   * @param legendId
   * @param version
   * @param request
   */
  update(
    legendId: string,
    version: number,
    request: SaveLegendVersionRequest,
  ): Promise<SaveLegendVersionResultResource> {
    return this._put(
      `v3/legends/${encodeURIComponent(legendId)}/versions/${version}`,
      request,
    )
  }

  /**
   * Saves the fields of the version that are in the request.
   *
   * @param legendId
   * @param version
   * @param request
   */
  patch(
    legendId: string,
    version: number,
    request: SaveLegendVersionRequest,
  ): Promise<SaveLegendVersionResultResource> {
    return this._patch(
      `v3/legends/${encodeURIComponent(legendId)}/versions/${version}`,
      request,
    )
  }

  /**
   * Publishes the version, which makes it the one new jobs use.
   *
   * @param legendId
   * @param version
   * @param request
   */
  publish(
    legendId: string,
    version: number,
    request: PublishLegendVersionRequest,
  ): Promise<LegendResource> {
    return this._post(
      `v3/legends/${encodeURIComponent(legendId)}/versions/${version}/publish`,
      request,
      toURLSearchParams(supportedEngineVersionParams),
    )
  }

  /**
   * Runs the version through the engine with sample input.
   *
   * @param legendId
   * @param version
   * @param request
   * @param signal
   */
  compute(
    legendId: string,
    version: number,
    request: ComputeLegendVersionRequest,
    signal?: AbortSignal,
  ): Promise<ComputeLegendVersionResultResource> {
    return this._post(
      `v3/legends/${encodeURIComponent(legendId)}/versions/${version}/compute`,
      request,
      undefined,
      { signal },
    )
  }
}

/**
 * The symbols the engine provides to formulas.
 */
export class EngineSymbolsApi extends ApiClient {
  /**
   * Gets the symbols.
   *
   * @param signal
   */
  get(signal?: AbortSignal): Promise<EngineSymbolsResource> {
    return this._get('v3/engine/symbols', undefined, { signal })
  }
}

/**
 * The personal organizer fields legends can prefill and collect.
 */
export class OrganizerBindingsApi extends ApiClient {
  /**
   * Gets the fields, by catalog.
   *
   * @param signal
   */
  get(signal?: AbortSignal): Promise<OrganizerBindingCatalogsResource> {
    return this._get('v3/engine/organizer-bindings', undefined, { signal })
  }
}
